import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, FailureBadge, Loading, Meter, useAsync } from '../components/common';
import type { OpsArea } from '../types';

const AREA_COPY: Record<
  OpsArea,
  { title: string; subtitle: string; blurb: string; empty: string }
> = {
  settlement_operations: {
    title: 'Settlement Operations',
    subtitle: 'DSP settlement round-trip — ready-to-settle through escrow sweep',
    blurb:
      'Open a function to see exactly what it does for each DSP parent: which bucket and file types it reads/writes, which Zoho Partner ids and Snowflake profile it uses, and how the runtime client resolves that parent from the published snapshot.',
    empty: 'No settlement-operations functions are registered.',
  },
  lead_scoring: {
    title: 'Lead Scoring',
    subtitle: 'Eligibility, LendAPI scoring, Zoho lead load and export',
    blurb:
      'Lead-side functions are parent-scoped through the same binding model. If a DSP is missing from a function’s enabled bindings, that parent is skipped for scoring/export — the matrix and each function’s per-DSP section make that visible.',
    empty: 'No lead-scoring functions are registered.',
  },
  general: {
    title: 'General Lambdas',
    subtitle: 'Shared platform tooling not owned by one pipeline',
    blurb:
      'Cross-cutting APIs and admin surfaces. Partner-aware rows still show which DSP profiles they can open; partner-agnostic rows explain that they do not take a per-DSP binding.',
    empty: 'No general functions are registered.',
  },
};

export default function LambdasPage({ opsArea }: { opsArea: OpsArea }) {
  const copy = AREA_COPY[opsArea];
  const { data, error, loading } = useAsync(
    () => api.listLambdas({ ops_area: opsArea }),
    [opsArea],
  );
  const [query, setQuery] = useState('');
  const [onlyPartnerAware, setOnlyPartnerAware] = useState(opsArea !== 'general');

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = query.trim().toLowerCase();
    return data.filter((row) => {
      if (onlyPartnerAware && !row.partner_aware) return false;
      if (!needle) return true;
      return (
        row.display_name.toLowerCase().includes(needle) ||
        row.lambda_key.includes(needle) ||
        row.code_path.toLowerCase().includes(needle) ||
        (row.operation_summary || '').toLowerCase().includes(needle)
      );
    });
  }, [data, onlyPartnerAware, query]);

  if (loading || !data) return <Loading error={error} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{copy.title}</h1>
          <p>{copy.subtitle}</p>
        </div>
      </div>

      <div className="callout info">{copy.blurb}</div>

      <div className="card">
        <div className="row" style={{ marginBottom: 14 }}>
          <label className="field">
            <span>Search</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="name, key, path, or what it does"
            />
          </label>
          <label className="inline" style={{ marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={onlyPartnerAware}
              onChange={(e) => setOnlyPartnerAware(e.target.checked)}
            />
            Partner-aware only
          </label>
        </div>

        {rows.length === 0 ? (
          <div className="callout">{copy.empty}</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Function</th>
                  <th>What it does</th>
                  <th>Trigger</th>
                  <th>DSPs enabled</th>
                  <th>Completeness</th>
                  <th>Failure mode</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.lambda_key}>
                    <td style={{ minWidth: 200 }}>
                      <Link to={`/lambdas/${row.lambda_key}`}>{row.display_name}</Link>
                      <div className="small muted mono">{row.lambda_key}</div>
                      <div className="chips" style={{ marginTop: 4 }}>
                        {row.required_for_active_parent && (
                          <Badge tone="danger">required</Badge>
                        )}
                        {!row.partner_aware && <Badge>partner-agnostic</Badge>}
                        <Badge tone={row.status === 'active' ? 'ok' : 'warn'}>{row.status}</Badge>
                      </div>
                    </td>
                    <td className="small" style={{ maxWidth: 360 }}>
                      {row.operation_summary || <span className="muted">—</span>}
                      <div className="mono muted" style={{ marginTop: 4 }}>
                        {row.code_path}
                      </div>
                    </td>
                    <td className="small">
                      <Badge tone="info">{row.trigger_type}</Badge>
                      <div className="mono muted" style={{ marginTop: 3, maxWidth: 260 }}>
                        {row.trigger_summary}
                      </div>
                    </td>
                    <td>
                      <div className="chips">
                        {row.partners_enabled.length === 0 ? (
                          <span className="muted small">none</span>
                        ) : (
                          row.partners_enabled.map((p) => (
                            <Link key={p} to={`/parents/${p}`}>
                              <Badge>{p}</Badge>
                            </Link>
                          ))
                        )}
                      </div>
                    </td>
                    <td>
                      <Meter value={row.config_completeness} />
                    </td>
                    <td>
                      <FailureBadge mode={row.failure_mode} />
                      {row.danger_flag_count > 0 && (
                        <div style={{ marginTop: 4 }}>
                          <Badge tone="danger">{row.danger_flag_count} danger flags</Badge>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="small muted" style={{ marginBottom: 0 }}>
          Showing {rows.length} of {data.length} functions in {copy.title}. Click a function for
          per-DSP usage detail.
        </p>
      </div>
    </>
  );
}
